export const MILESTONES = ["Turning point", "Color change", "First crack", "Second crack", "Drop"] as const;
export type TimedEvent = { name: string; elapsedMs: number };
export type RoastPhase = { name: "Drying" | "Maillard" | "Development"; durationMs: number | null; fraction: number | null };
export type PhaseAnalysis = { durationMs: number | null; phases: RoastPhase[]; issues: string[]; complete: boolean };

/** The interchange clock starts at charge. A file's final sample is not a drop event. */
export function analyzePhases(events: readonly TimedEvent[]): PhaseAnalysis {
  const issues: string[] = [];
  const times = new Map<string, number>();
  for (const name of MILESTONES) {
    const matches = events.filter(event => event.name === name);
    if (matches.length > 1) issues.push(`${name} is marked more than once.`);
    if (matches.length === 1) {
      const time = matches[0]!.elapsedMs;
      if (!Number.isFinite(time) || time <= 0) issues.push(`${name} must be after charge at 00:00.`);
      else times.set(name, time);
    }
  }
  let previous: { name: string; time: number } | null = null;
  for (const name of MILESTONES) {
    const time = times.get(name);
    if (time === undefined) continue;
    if (previous && time <= previous.time) issues.push(`${name} must be after ${previous.name.toLowerCase()}.`);
    previous = { name, time };
  }
  const drop = times.get("Drop") ?? null;
  const color = times.get("Color change") ?? null;
  const crack = times.get("First crack") ?? null;
  const valid = issues.length === 0;
  const durationMs = valid ? drop : null;
  const durations = [color, color !== null && crack !== null ? crack - color : null,
    crack !== null && drop !== null ? drop - crack : null];
  const phases = (["Drying", "Maillard", "Development"] as const).map((name, index) => {
    const duration = valid ? durations[index]! : null;
    return { name, durationMs: duration, fraction: duration !== null && durationMs !== null ? duration / durationMs : null };
  });
  return { durationMs, phases, issues, complete: valid && phases.every(phase => phase.fraction !== null) };
}

/** Validate only the changed milestone so legacy annotations can be repaired incrementally. */
export function validateMilestoneChange(events: readonly TimedEvent[], candidate: TimedEvent) {
  const index = MILESTONES.findIndex(name => name === candidate.name);
  if (index < 0) return;
  if (candidate.elapsedMs <= 0) throw new Error(`Move past 00:00 before marking ${candidate.name.toLowerCase()}.`);
  if (events.some(event => event.name === candidate.name)) throw new Error(`${candidate.name} is already marked. Edit its existing event.`);
  for (const event of events) {
    const other = MILESTONES.findIndex(name => name === event.name);
    if (other < 0) continue;
    if (other < index && event.elapsedMs >= candidate.elapsedMs) throw new Error(`${candidate.name} must be after ${event.name.toLowerCase()}.`);
    if (other > index && event.elapsedMs <= candidate.elapsedMs) throw new Error(`${candidate.name} must be before ${event.name.toLowerCase()}.`);
  }
}
